/**
 * Recompute plume boundaries from v3.2 horizon predictions.
 *
 * The plume polygon at "today" is computed by the backend: walk every well,
 * check measured_conc against the active reg library's MCL, take the convex
 * hull of wells that exceed. When the user slides into a future v3.2 horizon
 * we have to do the same thing — but with `predicted_conc_mgl` at that
 * horizon swapped in for the measured value, and re-hulled.
 *
 * The polygon must also update when the user changes the reg library, since
 * a different MCL changes which wells exceed. Both axes (slider position,
 * reg library) feed in through `wellHorizonOverrides` and the boundary's
 * own `mcl_ug_l` (which is already reg-library-aware from the parent fetch).
 *
 * Inputs:
 *   boundaries        — measured ContaminantBoundaryData[] for the current
 *                       reg library; each carries `mcl_ug_l` for its compound.
 *   overrides         — Map<well_name, Map<analyte_code_lower, mgL>> from
 *                       v3.2 timeline-predictions at the selected horizon.
 *
 * Output: ContaminantBoundaryData[] with:
 *   - wells_with_detections / wells_below_threshold re-bucketed by prediction
 *   - boundary.boundary replaced by the convex hull of the new exceeding set
 *   - max_concentration_ug_l recomputed
 */

import type { ContaminantBoundaryData } from '../../api/polygon';

/** Matches `WellDetection` from `api/polygon.ts` (re-shaped locally so this
 *  helper has no runtime dependency on the API module beyond the type). */
type WellPoint = {
  well_id: string;
  lat: number;
  lon: number;
  concentration: number;  // µg/L (post-conversion from mg/L)
  is_non_detect: boolean;
};

// ── Exceedance-scaled buffer (mirrors generate_dynamic_plume_boundary) ──────
// A well far above MCL pushes its polygon vertex further out (the true MCL
// contour lies beyond it); a well at MCL keeps the vertex hugging it.
const BUF_BASE_FT = 25;          // baseline at/below MCL
const BUF_PER_DECADE_FT = 75;    // +75 ft per 10x over MCL (10x -> 100 ft)
const BUF_MAX_FT = 300;          // cap
const FT_PER_DEG_LAT = 364000;

function exceedanceBufferFt(conc: number, mcl: number): number {
  if (!mcl || mcl <= 0 || !(conc > mcl)) return BUF_BASE_FT;
  return Math.min(BUF_MAX_FT, BUF_BASE_FT + BUF_PER_DECADE_FT * Math.log10(conc / mcl));
}

/** Convex hull preserving the well objects (so each vertex keeps its conc). */
function convexHullWells(points: WellPoint[]): WellPoint[] {
  const pts = points
    .filter(p => Number.isFinite(p.lat) && Number.isFinite(p.lon))
    .sort((a, b) => (a.lon - b.lon) || (a.lat - b.lat));
  if (pts.length < 3) return pts;
  const cross = (O: WellPoint, A: WellPoint, B: WellPoint) =>
    (A.lon - O.lon) * (B.lat - O.lat) - (A.lat - O.lat) * (B.lon - O.lon);
  const lower: WellPoint[] = [];
  for (const p of pts) {
    while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) lower.pop();
    lower.push(p);
  }
  const upper: WellPoint[] = [];
  for (let i = pts.length - 1; i >= 0; i--) {
    const p = pts[i];
    while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) upper.pop();
    upper.push(p);
  }
  return lower.slice(0, -1).concat(upper.slice(0, -1));
}

/** Hull of exceeding wells, each vertex pushed out from the geometric center by
 *  its OWN exceedance buffer — a sharp polygon that bulges at hot wells and hugs
 *  near-MCL ones. Mirrors the backend generate_dynamic_plume_boundary. */
function bufferedExceedanceHull(wells: WellPoint[], mcl: number): [number, number][] {
  const hullW = convexHullWells(wells);
  if (hullW.length < 3) return hullW.map(w => [w.lat, w.lon] as [number, number]);
  const cLat = wells.reduce((s, w) => s + w.lat, 0) / wells.length;
  const cLon = wells.reduce((s, w) => s + w.lon, 0) / wells.length;
  const ftPerDegLon = FT_PER_DEG_LAT * Math.cos((cLat * Math.PI) / 180);
  return hullW.map(w => {
    const buf = exceedanceBufferFt(w.concentration, mcl);
    const dxFt = (w.lon - cLon) * ftPerDegLon;
    const dyFt = (w.lat - cLat) * FT_PER_DEG_LAT;
    const dist = Math.hypot(dxFt, dyFt);
    if (dist <= 0) return [w.lat, w.lon] as [number, number];
    const scale = (dist + buf) / dist;
    return [cLat + (dyFt * scale) / FT_PER_DEG_LAT, cLon + (dxFt * scale) / ftPerDegLon] as [number, number];
  });
}

/**
 * Returns the active prediction (in µg/L) for a (well, compound) pair, or
 * `null` when no v3.2 forecast exists. The boundary's `mcl_ug_l` and the
 * caller's `wellHorizonOverrides` Map are passed in to keep this pure.
 *
 * v3.2 keys analyte codes by lowercase. Falls through compound aliases so
 * "TPH-GRO" and "C6-C12" both resolve to the same prediction key.
 */
function lookupPredictionUgL(
  overrides: Map<string, Map<string, number>>,
  wellName: string,
  compound: string,
): number | null {
  const byAnalyte = overrides.get(wellName);
  if (!byAnalyte) return null;
  const candidates = [
    compound.toLowerCase(),
    compound.toLowerCase().replace(/^(tph[- ]?)/, ''),
  ];
  for (const k of candidates) {
    const mgl = byAnalyte.get(k);
    if (typeof mgl === 'number' && Number.isFinite(mgl) && mgl >= 0) {
      return mgl * 1000;  // mg/L → µg/L
    }
  }
  return null;
}

/**
 * Recompute every boundary's polygon against the CURRENT reg library's MCL —
 * optionally swapping measured concentrations for v3.2 horizon predictions.
 *
 * Single helper that covers all three cases:
 *   1. **Today, reg library changed** — `overrides` empty, `currentMclByCompound`
 *      supplied. Wells stay at their measured concs, but the polygon re-hulls
 *      against the new MCL.
 *   2. **Historical scrub** — same as above, just with snapshot wells. Fixes
 *      the case where a snapshot's `boundary.mcl_ug_l` was frozen at compute
 *      time so the polygon you saw didn't reflect the current dropdown.
 *   3. **v3.2 future horizon** — `overrides` populated, `currentMclByCompound`
 *      still applied so the predicted concs are scored against the active
 *      reg library.
 *
 * When `currentMclByCompound` is provided, it overrides `boundary.mcl_ug_l`
 * for the partition. Falls back to the boundary's own MCL otherwise (so the
 * helper degrades cleanly when called with neither arg).
 */
export function applyHorizonOverridesToBoundaries(
  boundaries: ContaminantBoundaryData[] | null | undefined,
  overrides: Map<string, Map<string, number>>,
  selectedHorizonDays: number,
  currentMclByCompound?: Map<string, number> | null,
  wellLocationByName?: Map<string, { lat: number; lon: number }> | null,
): ContaminantBoundaryData[] {
  if (!boundaries || boundaries.length === 0) return [];
  // Short-circuit only when there's literally nothing to do: no overrides AND
  // no current MCL override. Otherwise the rehull pass still runs (cheap).
  const hasOverrides = selectedHorizonDays > 0 && overrides.size > 0;
  const hasMclMap = !!currentMclByCompound && currentMclByCompound.size > 0;
  if (!hasOverrides && !hasMclMap) return boundaries;

  const out: ContaminantBoundaryData[] = [];

  for (const b of boundaries) {
    const compound = b.compound;
    // Prefer the active reg library's MCL when supplied; otherwise fall back
    // to whatever the boundary was computed with.
    const mcl = (currentMclByCompound?.get(compound.toLowerCase())
      ?? currentMclByCompound?.get(compound)
      ?? b.mcl_ug_l
      ?? 0);

    // Dedupe wells across the two arrays — same well can appear in both
    // (a non-detect appears in `wells_below_threshold` while a detect of the
    // same well at the same compound appears in `wells_with_detections`).
    const wellById = new Map<string, WellPoint>();
    const merge = (arr: typeof b.wells_with_detections) => {
      for (const w of arr || []) {
        if (!w?.well_id || typeof w.lat !== 'number' || typeof w.lon !== 'number') continue;
        wellById.set(w.well_id, {
          well_id: w.well_id,
          lat: w.lat,
          lon: w.lon,
          concentration: w.concentration,
          is_non_detect: !!w.is_non_detect,
        });
      }
    };
    merge(b.wells_with_detections);
    merge(b.wells_below_threshold);

    // Track which wells came from the override map vs from the boundary, so
    // we can drop seeded-only wells if they have no forecast for the active
    // compound (no measurement and no forecast = nothing defensible to plot).
    const seededOnly = new Set<string>();

    // Seed wells that only appear in the override map — wells that v3.2
    // newly predicts to exceed but never had a measurement, so they aren't
    // in either of the boundary's well arrays. Without this seeding the
    // convex hull only ever encloses wells that were already sampled,
    // which is why the future plume "didn't follow" the red-flashing wells.
    if (hasOverrides && wellLocationByName) {
      for (const wellName of overrides.keys()) {
        if (wellById.has(wellName)) continue;
        const loc = wellLocationByName.get(wellName);
        if (!loc) continue;
        wellById.set(wellName, {
          well_id: wellName,
          lat: loc.lat,
          lon: loc.lon,
          concentration: 0,
          is_non_detect: true,
        });
        seededOnly.add(wellName);
      }
    }

    const newAbove: WellPoint[] = [];
    const newBelow: WellPoint[] = [];
    let maxConc = 0;

    for (const w of wellById.values()) {
      const predUgL = hasOverrides ? lookupPredictionUgL(overrides, w.well_id, compound) : null;

      // Effective concentration for this (well, compound):
      //  - if a v3.2 override exists at the selected horizon → use it.
      //  - else fall back to the measured value from the boundary so wells
      //    currently exceeding stay in the polygon even when v3.2 doesn't
      //    have a row for this exact (well, compound, horizon) tuple.
      //  - drop ONLY wells we seeded from overrides but that have no
      //    forecast for this compound (no measurement, no forecast).
      let effectiveUgL: number;
      let isNd: boolean;
      if (predUgL != null) {
        effectiveUgL = predUgL;
        isNd = false;  // A forecast displaces ND semantics.
      } else if (seededOnly.has(w.well_id)) {
        // Seeded from overrides but no forecast for THIS compound → no data.
        continue;
      } else {
        // Measured well with no forecast at this horizon — carry forward
        // the measurement so the polygon stays anchored to currently-known
        // exceedances. Reverting wells to clean just because v3.2 skipped a
        // (well, compound) cell is the bug we're fixing.
        effectiveUgL = w.concentration;
        isNd = w.is_non_detect;
      }

      if (!Number.isFinite(effectiveUgL) || effectiveUgL < 0) continue;

      const updated: WellPoint = { ...w, concentration: effectiveUgL, is_non_detect: isNd };
      if (effectiveUgL > mcl) {
        newAbove.push(updated);
        if (effectiveUgL > maxConc) maxConc = effectiveUgL;
      } else {
        newBelow.push(updated);
      }
    }

    // Polygon = hull of the exceeding-at-horizon set, each vertex pushed out by
    // its MCL-exceedance buffer (hot wells bulge, near-MCL wells hug). If fewer
    // than 3 wells exceed, leave it empty — leaflet renders the wells only.
    const hull = newAbove.length >= 3
      ? bufferedExceedanceHull(newAbove, mcl)
      : [];

    out.push({
      ...b,
      mcl_ug_l: mcl,  // surface the active reg library's MCL on the row
      wells_with_detections: newAbove,
      wells_below_threshold: newBelow,
      boundary: hull,
      max_concentration_ug_l: maxConc || b.max_concentration_ug_l,
      well_count: newAbove.length + newBelow.length,
    });
  }

  return out;
}
